import { useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useUserContext } from '@/contexts/UserContext';
import { logger } from '@/utils/logger';

// Event types — only types actively used in code and/or with DB records
export type AnalyticsEventType =
  // Profile Discovery
  | 'profile_impression' | 'profile_click' | 'profile_view'
  // Profile Engagement
  | 'calendar_click' | 'phone_click' | 'website_click' | 'linkedin_click'
  | 'favorite_add' | 'favorite_remove' | 'message_click'
  // Opportunities
  | 'opportunity_sent' | 'opportunity_verified' | 'opportunity_viewed' | 'opportunity_contacted'
  // Content
  | 'publication_impression' | 'publication_view' | 'publication_read'
  // Search & Chat
  | 'search_submit' | 'search_result_click' | 'hero_chat_submit'
  | 'location_select' | 'location_detection'
  // Chat Analytics
  | 'chat_session_start' | 'chat_message_sent' | 'chat_response_received'
  | 'chat_expert_click' | 'chat_expert_recommend'
  // Help Center
  | 'faq_click' | 'help_center_chat'
  | 'help_widget_open' | 'help_widget_suggestion_click'
  | 'help_password_reset' | 'help_verification_resent' | 'help_magic_link_sent'
  | 'help_chat_escalation' | 'help_ticket_created' | 'help_transcript_sent'
  | 'help_faq_helpful' | 'help_faq_not_helpful'
  // Forms
  | 'support_form_submit' | 'contact_form_submit' | 'claim_profile_submit' | 'help_center_form_submit'
  // Registration & Auth
  | 'registration_start' | 'registration_step' | 'registration_complete'
  | 'login' | 'signup' | 'password_reset'
  // Compose
  | 'compose_open'
  // Email
  | 'email_sent' | 'email_opened' | 'email_clicked' | 'email_queued'
  // Page Views & Navigation
  | 'page_view';

export type AnalyticsEventCategory =
  | 'profile' | 'engagement' | 'opportunity' | 'content'
  | 'search' | 'chat' | 'form' | 'auth' | 'email'
  | 'navigation' | 'session';

interface TrackEventParams {
  eventType: AnalyticsEventType;
  eventCategory: AnalyticsEventCategory;
  targetUserId?: string;
  targetContentId?: string;
  sourcePage?: string;
  metadata?: Record<string, unknown>;
}

// Generate or retrieve session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Session-scoped dedup for impression events (shared across all hook instances)
// Resets when the tab/page is closed (module-level variable)
const sessionImpressions = new Set<string>();

// Bot detection — skip tracking for known crawlers/bots
const BOT_UA_PATTERNS = /bot|crawl|spider|slurp|mediapartners|facebookexternalhit|linkedinbot|twitterbot|whatsapp|telegram|baiduspider|yandex|duckduckbot|sogou|exabot|ia_archiver|semrush|ahrefs|mj12bot|dotbot|petalbot|bytespider|gptbot|claudebot|anthropic|perplexity|cohere-ai/i;

const isBot = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return BOT_UA_PATTERNS.test(navigator.userAgent);
};

export const useAnalyticsTracking = () => {
  const { user } = useAuth();
  const { currentLocation } = useUserContext();
  const pendingEvents = useRef<Set<string>>(new Set());

  const trackEvent = useCallback((params: TrackEventParams) => {
    // Only track in production (after publish)
    if (!import.meta.env.PROD) return;
    // Skip bot traffic entirely
    if (isBot()) return;
    const {
      eventType,
      eventCategory,
      targetUserId,
      targetContentId,
      sourcePage,
      metadata = {}
    } = params;

    // Session-scoped dedup for impressions and location_detection
    if (eventType.endsWith('_impression') || eventType === 'location_detection') {
      const dedupKey = `${eventType}_${targetUserId || targetContentId || 'global'}`;
      if (sessionImpressions.has(dedupKey)) return;
      sessionImpressions.add(dedupKey);
    }

    // Dedupe: prevent tracking same event multiple times in quick succession
    const eventKey = `${eventType}_${targetUserId || ''}_${targetContentId || ''}_${Date.now().toString().slice(0, -3)}`;
    if (pendingEvents.current.has(eventKey)) {
      return;
    }
    pendingEvents.current.add(eventKey);
    
    // Clean up old keys after 1 second
    setTimeout(() => {
      pendingEvents.current.delete(eventKey);
    }, 1000);

    const payload = {
      actorId: user?.id || null,
      targetUserId: targetUserId || null,
      targetContentId: targetContentId || null,
      eventType,
      eventCategory,
      sourcePage: sourcePage || window.location.pathname,
      sourceLocation: currentLocation || null,
      sessionId: getSessionId(),
      metadata: {
        ...metadata,
        userAgent: navigator.userAgent,
        referrer: document.referrer,
        timestamp: new Date().toISOString()
      }
    };

    // Fire-and-forget: don't await to prevent blocking page load
    // Use AbortController with 5s timeout to prevent hung connections
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    supabase.functions.invoke('track-analytics-event', {
      body: payload
    }).then(({ data, error }) => {
      clearTimeout(timeoutId);
      if (error) {
        logger.debug('Analytics tracking failed silently', { eventType });
      } else {
        logger.debug('Analytics event tracked', { eventType, eventId: data?.eventId });
      }
    }).catch(() => {
      clearTimeout(timeoutId);
      // Silent fail - don't log timeout errors to console
    });
  }, [user?.id, currentLocation]);

  // Convenience methods for common events
  const trackProfileImpression = useCallback((expertId: string, sourcePage?: string) => {
    trackEvent({
      eventType: 'profile_impression',
      eventCategory: 'profile',
      targetUserId: expertId,
      sourcePage
    });
  }, [trackEvent]);

  const trackProfileClick = useCallback((expertId: string, sourcePage?: string) => {
    trackEvent({
      eventType: 'profile_click',
      eventCategory: 'profile',
      targetUserId: expertId,
      sourcePage
    });
  }, [trackEvent]);

  const trackProfileView = useCallback((expertId: string) => {
    trackEvent({
      eventType: 'profile_view',
      eventCategory: 'profile',
      targetUserId: expertId
    });
  }, [trackEvent]);

  const trackPublicationView = useCallback((publicationId: string, authorId?: string) => {
    trackEvent({
      eventType: 'publication_view',
      eventCategory: 'content',
      targetUserId: authorId,
      targetContentId: publicationId
    });
  }, [trackEvent]);

  const trackPublicationImpression = useCallback((publicationId: string, authorId?: string, sourcePage?: string) => {
    trackEvent({
      eventType: 'publication_impression',
      eventCategory: 'content',
      targetUserId: authorId,
      targetContentId: publicationId,
      sourcePage
    });
  }, [trackEvent]);

  const trackPublicationRead = useCallback((publicationId: string, authorId?: string, metadata?: { readDuration: number; scrollDepth: number; isLocalized?: boolean }) => {
    trackEvent({
      eventType: 'publication_read',
      eventCategory: 'content',
      targetUserId: authorId,
      targetContentId: publicationId,
      metadata
    });
  }, [trackEvent]);

  const trackSearchSubmit = useCallback((query: string, resultCount: number, options?: { source?: string; locale?: string }) => {
    trackEvent({
      eventType: 'search_submit',
      eventCategory: 'search',
      metadata: { query, resultCount, source: options?.source, locale: options?.locale }
    });
  }, [trackEvent]);

  const trackSearchResultClick = useCallback((expertId: string, position: number) => {
    trackEvent({
      eventType: 'search_result_click',
      eventCategory: 'search',
      targetUserId: expertId,
      metadata: { position }
    });
  }, [trackEvent]);

  const trackPublicationSearchClick = useCallback((publicationId: string, position: number, query: string) => {
    trackEvent({
      eventType: 'search_result_click',
      eventCategory: 'search',
      targetContentId: publicationId,
      metadata: { position, query, source: 'header' }
    });
  }, [trackEvent]);

  const trackFavoriteAdd = useCallback((expertId: string) => {
    trackEvent({
      eventType: 'favorite_add',
      eventCategory: 'engagement',
      targetUserId: expertId
    });
  }, [trackEvent]);

  const trackFavoriteRemove = useCallback((expertId: string) => {
    trackEvent({
      eventType: 'favorite_remove',
      eventCategory: 'engagement',
      targetUserId: expertId
    });
  }, [trackEvent]);

  const trackPublicationFavoriteAdd = useCallback((publicationId: string) => {
    trackEvent({
      eventType: 'favorite_add',
      eventCategory: 'engagement',
      targetContentId: publicationId
    });
  }, [trackEvent]);

  const trackPublicationFavoriteRemove = useCallback((publicationId: string) => {
    trackEvent({
      eventType: 'favorite_remove',
      eventCategory: 'engagement',
      targetContentId: publicationId
    });
  }, [trackEvent]);

  const trackMessageClick = useCallback((expertId: string) => {
    trackEvent({
      eventType: 'message_click',
      eventCategory: 'engagement',
      targetUserId: expertId
    });
  }, [trackEvent]);

  const trackOpportunitySent = useCallback((expertId: string, opportunityId: string) => {
    trackEvent({
      eventType: 'opportunity_sent',
      eventCategory: 'opportunity',
      targetUserId: expertId,
      targetContentId: opportunityId
    });
  }, [trackEvent]);

  const trackHeroChatSubmit = useCallback((query: string, location?: string) => {
    trackEvent({
      eventType: 'hero_chat_submit',
      eventCategory: 'chat',
      metadata: { query, location }
    });
  }, [trackEvent]);

  const trackLocationSelect = useCallback((location: string) => {
    trackEvent({
      eventType: 'location_select',
      eventCategory: 'chat',
      metadata: { location }
    });
  }, [trackEvent]);

  // Location Detection Analytics - tracks how users' locations are determined
  const trackLocationDetection = useCallback((
    location: string,
    source: 'ip' | 'url' | 'account' | 'session' | 'user_selection' | 'default',
    metadata?: { previousLocation?: string; ipCountry?: string; isFirstVisit?: boolean; isExplicitUserChoice?: boolean }
  ) => {
    trackEvent({
      eventType: 'location_detection',
      eventCategory: 'navigation',
      metadata: {
        location,
        detectionSource: source,
        ...metadata
      }
    });
  }, [trackEvent]);

  // Chat Analytics
  const trackChatSessionStart = useCallback((sessionId: string, location?: string, intentMode?: string) => {
    trackEvent({
      eventType: 'chat_session_start',
      eventCategory: 'chat',
      metadata: { chatSessionId: sessionId, location, intentMode }
    });
  }, [trackEvent]);

  const trackChatMessageSent = useCallback((sessionId: string, messageLength: number, messageIndex: number) => {
    trackEvent({
      eventType: 'chat_message_sent',
      eventCategory: 'chat',
      metadata: { chatSessionId: sessionId, messageLength, messageIndex }
    });
  }, [trackEvent]);

  const trackChatResponseReceived = useCallback((
    sessionId: string, 
    responseLength: number, 
    hasExperts: boolean, 
    expertCount: number,
    matchedTopics?: string[],
    matchedTopicIds?: string[]
  ) => {
    trackEvent({
      eventType: 'chat_response_received',
      eventCategory: 'chat',
      metadata: { 
        chatSessionId: sessionId, 
        responseLength, 
        hasExperts, 
        expertCount,
        matchedTopics: matchedTopics || [],
        matchedTopicIds: matchedTopicIds || [],
        topicCount: matchedTopics?.length || 0
      }
    });
  }, [trackEvent]);

  const trackChatExpertClick = useCallback((expertId: string, sessionId: string, position: number) => {
    trackEvent({
      eventType: 'chat_expert_click',
      eventCategory: 'chat',
      targetUserId: expertId,
      metadata: { chatSessionId: sessionId, position }
    });
  }, [trackEvent]);

  const trackChatExpertRecommend = useCallback((sessionId: string, expertIds: string[], topics: string[]) => {
    trackEvent({
      eventType: 'chat_expert_recommend',
      eventCategory: 'chat',
      metadata: { chatSessionId: sessionId, expertIds, topics, expertCount: expertIds.length }
    });
  }, [trackEvent]);

  const trackCalendarClick = useCallback((expertId: string) => {
    trackEvent({
      eventType: 'calendar_click',
      eventCategory: 'engagement',
      targetUserId: expertId
    });
  }, [trackEvent]);

  const trackPhoneClick = useCallback((expertId: string) => {
    trackEvent({
      eventType: 'phone_click',
      eventCategory: 'engagement',
      targetUserId: expertId
    });
  }, [trackEvent]);

  const trackWebsiteClick = useCallback((expertId: string) => {
    trackEvent({
      eventType: 'website_click',
      eventCategory: 'engagement',
      targetUserId: expertId
    });
  }, [trackEvent]);

  const trackLinkedInClick = useCallback((expertId: string) => {
    trackEvent({
      eventType: 'linkedin_click',
      eventCategory: 'engagement',
      targetUserId: expertId
    });
  }, [trackEvent]);

  const trackAuth = useCallback((type: 'login' | 'signup' | 'password_reset') => {
    trackEvent({
      eventType: type,
      eventCategory: 'auth'
    });
  }, [trackEvent]);

  const trackRegistration = useCallback((step: 'start' | 'step' | 'complete', stepNumber?: number) => {
    const eventType = step === 'start' ? 'registration_start' 
      : step === 'complete' ? 'registration_complete' 
      : 'registration_step';
    
    trackEvent({
      eventType,
      eventCategory: 'auth',
      metadata: stepNumber !== undefined ? { stepNumber } : {}
    });
  }, [trackEvent]);

  const trackFormSubmit = useCallback((formType: 'support' | 'contact' | 'claim_profile' | 'help_center') => {
    const eventType = formType === 'support' ? 'support_form_submit'
      : formType === 'contact' ? 'contact_form_submit'
      : formType === 'help_center' ? 'help_center_form_submit'
      : 'claim_profile_submit';
    
    trackEvent({
      eventType,
      eventCategory: 'form'
    });
  }, [trackEvent]);

  return {
    trackEvent,
    // Profile
    trackProfileImpression,
    trackProfileClick,
    trackProfileView,
    // Content
    trackPublicationView,
    trackPublicationImpression,
    trackPublicationRead,
    // Search
    trackSearchSubmit,
    trackSearchResultClick,
    trackPublicationSearchClick,
    // Engagement
    trackFavoriteAdd,
    trackFavoriteRemove,
    trackPublicationFavoriteAdd,
    trackPublicationFavoriteRemove,
    trackMessageClick,
    trackCalendarClick,
    trackPhoneClick,
    trackWebsiteClick,
    trackLinkedInClick,
    // Opportunity
    trackOpportunitySent,
    // Chat
    trackHeroChatSubmit,
    trackLocationSelect,
    trackLocationDetection,
    // Chat Analytics
    trackChatSessionStart,
    trackChatMessageSent,
    trackChatResponseReceived,
    trackChatExpertClick,
    trackChatExpertRecommend,
    // Auth
    trackAuth,
    trackRegistration,
    // Forms
    trackFormSubmit
  };
};
